import { useCallback, useEffect, useRef, useState } from 'react'
import { Download, FileUp, Sparkles, X } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { api } from '@/lib/axios'
import { bestaetige } from '@/lib/dialoge'
import { downloadFile } from '@/lib/download'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'
import type { Calculation } from '../types/calculation.types'
import { KiVorschlaege } from './KiVorschlaege'

type Faehigkeit = 'READ' | 'V3' | 'V4' | 'V6' | 'CHECK'
interface Auftrag {
  id: string
  seq: number
  capability: Faehigkeit
  status: 'OPEN' | 'RESULT' | 'APPLIED' | 'CLOSED' | 'EXPIRED'
  createdAt: string
  fileIds: string[]
  positionIds: string[]
  resultCount: number
  runId: string | null
  runStatus: string | null
  runError: string | null
  openLines: number
}

const FAEHIGKEIT: Record<Faehigkeit, string> = {
  READ: 'Unterlagen lesen',
  V3: 'Kalkulation aus Unterlagen',
  V4: 'LV vorkalkulieren',
  V6: 'Bieterlücken ausfüllen',
  CHECK: 'Prüfung vor der Abgabe',
}
const STATUS: Record<Auftrag['status'], string> = { OPEN: 'wartet auf Ergebnis', RESULT: 'Ergebnis eingetroffen', APPLIED: 'erledigt', CLOSED: 'geschlossen', EXPIRED: 'abgelaufen' }
const basis = (id: string) => `/calculations/${id}/ai-tasks`

async function lade(pfad: string, name: string) {
  const r = await api.get<ArrayBuffer>(pfad, { responseType: 'arraybuffer' })
  const kopf = String(r.headers['content-disposition'] ?? '')
  const aus = /filename="([^"]+)"/.exec(kopf)?.[1]
  downloadFile(r.data, aus ? decodeURIComponent(aus) : name, String(r.headers['content-type'] ?? 'application/octet-stream').split(';')[0])
}

/**
 * AP-37 Detailblatt N 3a.4, 15. (N-X) — **KI-Aufträge** im Reiter Dokumente: Auftrag für die
 * eigene KI anlegen, Paket laden (ZIP — OneDrive folgt mit N-O), `ergebnis.json` hochladen,
 * Prüfbericht laden, Vorschläge ansehen, schließen. Symphenia sendet dabei an keinen
 * KI-Anbieter; die Unterlagen gehen an die KI, die der Benutzer wählt (3a.6).
 */
export function KiAuftraege({ kalkulation, onGeaendert, vorwahl }: { kalkulation: Calculation; onGeaendert: () => void; vorwahl?: string | null }) {
  const [auftraege, setAuftraege] = useState<Auftrag[]>([])
  // N8: „Starten" im Assistenten öffnet den neuen Auftrag mit der genannten Aufgabe.
  const gewaehlt = vorwahl && vorwahl in FAEHIGKEIT ? (vorwahl as Faehigkeit) : null
  const [neu, setNeu] = useState(!!gewaehlt && kalkulation.writable)
  const [faehigkeit, setFaehigkeit] = useState<Faehigkeit>(gewaehlt ?? (kalkulation.hasLv ? 'V4' : 'V3'))
  const [bestaetigt, setBestaetigt] = useState(false)
  const [vorschlaege, setVorschlaege] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const hochladen = useRef<HTMLInputElement>(null)
  const [ziel, setZiel] = useState<string | null>(null)
  const darf = kalkulation.writable

  const laden = useCallback(() => {
    api
      .get<{ data: Auftrag[] }>(basis(kalkulation.id))
      .then((r) => setAuftraege(r.data.data))
      .catch((e) => setFehler(apiErrorText(e, 'Die KI-Aufträge ließen sich nicht laden.')))
  }, [kalkulation.id])
  useEffect(laden, [laden])

  async function tu(schritt: () => Promise<unknown>) {
    setLaeuft(true)
    try {
      setFehler(null)
      await schritt()
      laden()
      onGeaendert()
    } catch (e) {
      const d = (e as { response?: { data?: { errors?: string[] } } }).response?.data
      setFehler(`${apiErrorText(e, 'Das ließ sich nicht ausführen.')}${d?.errors?.length ? ` — ${d.errors.slice(0, 3).join('; ')}` : ''}`)
      laden()
    } finally {
      setLaeuft(false)
    }
  }

  const anlegen = () =>
    tu(async () => {
      const r = await api.post<{ data: Auftrag }>(basis(kalkulation.id), { capability: faehigkeit, transport: 'ZIP' })
      setNeu(false)
      setBestaetigt(false)
      await lade(`${basis(kalkulation.id)}/${r.data.data.id}/package`, `Auftrag-${r.data.data.seq}.zip`)
    })

  const faehigkeiten: Faehigkeit[] = kalkulation.hasLv ? ['V4', 'V6', 'CHECK', 'READ'] : ['V3', 'CHECK', 'READ']
  return (
    <div className={formCardSm}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className={cardTitle}>KI-Aufträge</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Aufträge für die eigene KI (Claude, ChatGPT, Copilot …): Paket laden, der KI geben, <code>ergebnis.json</code> hier hochladen. Symphenia prüft es und zeigt die Vorschläge.
          </p>
        </div>
        {darf && !neu && (
          <button type="button" className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground" onClick={() => setNeu(true)}>
            <Sparkles className="h-4 w-4" /> Auftrag für eigene KI …
          </button>
        )}
      </div>
      {neu && (
        <div className="mt-3 space-y-2 rounded-md border border-border/60 p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-medium">Neuer Auftrag</span>
            <button type="button" className="text-muted-foreground" onClick={() => setNeu(false)} aria-label="Schließen">
              <X className="h-4 w-4" />
            </button>
          </div>
          <label className="block">
            <span className="block text-xs text-muted-foreground">Aufgabe</span>
            <select className={`${inlineInput} !w-auto`} value={faehigkeit} onChange={(e) => setFaehigkeit(e.target.value as Faehigkeit)}>
              {faehigkeiten.map((f) => (
                <option key={f} value={f}>
                  {FAEHIGKEIT[f]}
                </option>
              ))}
            </select>
          </label>
          <p className="text-xs text-muted-foreground">Ins Paket kommen alle Positionen und alle Unterlagen, die nicht als „nicht an die KI" gekennzeichnet sind.</p>
          <label className="flex items-start gap-2 text-xs">
            <input type="checkbox" className="mt-0.5" checked={bestaetigt} onChange={(e) => setBestaetigt(e.target.checked)} />
            Die Unterlagen werden heruntergeladen und von einer KI meiner Wahl gelesen; was diese KI damit tut, regelt mein Vertrag mit ihrem Anbieter.
          </label>
          <button type="button" className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-primary-foreground disabled:opacity-50" disabled={!bestaetigt || laeuft} onClick={() => void anlegen()}>
            <Download className="h-4 w-4" /> Anlegen und Paket laden
          </button>
        </div>
      )}
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
      <input
        ref={hochladen}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          e.target.value = ''
          if (!f || !ziel) return
          const fd = new FormData()
          fd.append('file', f)
          void tu(() => api.post(`${basis(kalkulation.id)}/${ziel}/result`, fd))
        }}
      />
      {auftraege.length > 0 && (
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-1 pr-2">Nr.</th>
              <th className="py-1 pr-2">Aufgabe</th>
              <th className="py-1 pr-2">angelegt</th>
              <th className="py-1 pr-2">Stand</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {auftraege.map((a) => (
              <tr key={a.id} className="border-b border-border/60 align-top">
                <td className="py-1.5 pr-2 tabular-nums">{a.seq}</td>
                <td className="py-1.5 pr-2">{FAEHIGKEIT[a.capability]}</td>
                <td className="py-1.5 pr-2 text-muted-foreground">{new Date(a.createdAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</td>
                <td className="py-1.5 pr-2">
                  {STATUS[a.status]}
                  {a.openLines > 0 && <span className="text-muted-foreground"> · {a.openLines} Vorschläge offen</span>}
                  {a.runStatus === 'REJECTED' && <span className="block text-xs text-destructive">letztes Ergebnis abgelehnt — Prüfbericht</span>}
                </td>
                <td className="py-1.5 text-right">
                  <div className="flex flex-wrap justify-end gap-2 text-xs">
                    <button type="button" className="text-primary hover:underline" onClick={() => void tu(() => lade(`${basis(kalkulation.id)}/${a.id}/package`, `Auftrag-${a.seq}.zip`))}>
                      Paket
                    </button>
                    {darf && (a.status === 'OPEN' || a.status === 'RESULT') && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-0.5 text-primary hover:underline"
                        onClick={() => {
                          setZiel(a.id)
                          hochladen.current?.click()
                        }}
                      >
                        <FileUp className="h-3 w-3" /> Ergebnis hochladen
                      </button>
                    )}
                    {a.resultCount > 0 || a.runStatus === 'REJECTED' ? (
                      <button type="button" className="text-primary hover:underline" onClick={() => void tu(() => lade(`${basis(kalkulation.id)}/${a.id}/report`, `pruefbericht-${a.seq}.md`))}>
                        Prüfbericht
                      </button>
                    ) : null}
                    {a.openLines > 0 && (
                      <button type="button" className="text-primary hover:underline" onClick={() => setVorschlaege(true)}>
                        Vorschläge
                      </button>
                    )}
                    {darf && (a.status === 'OPEN' || a.status === 'RESULT') && (
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={async () => {
                          if (await bestaetige(`Auftrag ${a.seq} schließen? Offene Vorschläge werden verworfen.`, { ok: 'Schließen' })) void tu(() => api.post(`${basis(kalkulation.id)}/${a.id}/close`))
                        }}
                      >
                        schließen
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {vorschlaege && <KiVorschlaege kalkulationId={kalkulation.id} darf={darf} onClose={() => setVorschlaege(false)} onGeaendert={() => (laden(), onGeaendert())} />}
    </div>
  )
}
